import { z } from 'zod'

/**
 * Payload schema for one extracted student answer sheet — the PAYLOAD only,
 * matching `ExtractedSheet` (`@ztechapps/quickscore.contract`). `AnalyzeImage`/`AnalyzeImages`
 * wrap it into their own `{ data, confidence }` envelope via `wrapAnalyzeOutputSchema`; this
 * file MUST NOT redeclare that envelope.
 *
 * ONE shared schema serves both call shapes — `ResolveScanRoute.outputSchema` is a single
 * field passed unchanged to `ProcessScan`'s single-sheet `AnalyzeImage` call and to
 * `ProcessBatchScan`'s multi-sheet `AnalyzeImages` call; `wrapAnalyzeOutputSchema`'s
 * `asArray` flag is what turns this same per-sheet shape into `AnalyzeImages`' index-aligned
 * array envelope, so no separate "batch" schema is declared here (`code-rule.md` → DRY).
 *
 * ONE shared schema also serves both first-party providers — Gemini and OpenAI are
 * the only two `VisionExtractorProviderEnum` branches `ResolveScanRoute` supplies this to;
 * forking a per-provider variant is forbidden.
 *
 * Every property is REQUIRED-AND-NULLABLE, never `.optional()` — OpenAI strict mode forbids
 * an optional property, while Gemini's dialect accepts the nullable-but-required form
 * unchanged. The answer row is a HOMOGENEOUS STRING array — a positional tuple compiles to a
 * keyword neither dialect's strict mode accepts — so its `[questionNumber, answer, confidence]`
 * contract is documented via `.describe(...)` instead of a positional type, and coerced back to
 * numbers afterwards by `toExtractedSheet` (`@ztechapps/quickscore.common`).
 *
 * The row length is enforced with `.min(3)` here, NOT pinned to `.length(3)`. Measured
 * production payloads show a vendor sometimes collapsing every question/answer pair into ONE
 * oversized row (`["1","D","2","C",…]`, occasionally with one trailing bare confidence) instead
 * of emitting one row per pair. A `.length(3)` constraint rejects that single row, and Zod fails
 * the WHOLE `answers` array on one bad element — so `generateText` degraded the ENTIRE sheet to
 * `null` before `toExtractedSheet` (which already owns every other row-level coercion for this
 * payload) ever saw the data to attempt a recovery. `.min(3)` lets the oversized row reach the
 * parser instead, which reshapes an unambiguous flattened row back into proper triples and drops
 * anything it can't confidently reshape — moving the length check downstream turns "one bad row
 * loses the whole sheet" into "one bad row is repaired or, worst case, dropped alone".
 *
 * `confidence` is this SHEET's own read confidence (0-1) — distinct from the `{data,
 * confidence}` envelope `wrapAnalyzeOutputSchema` wraps this schema into, whose `confidence` is
 * one aggregate value per AI call. For a single-image call the two happen to be the same
 * sheet, but for `ProcessBatchScan`'s multi-image call the envelope's confidence is shared by
 * every sheet in the chunk while this field lets each sheet report its own legibility. Required-
 * and-nullable for the same OpenAI-strict-mode reason as `studentName`.
 */
export const EXTRACTED_SHEET_SCHEMA = z.object({
  studentName: z.string().nullable(),
  confidence: z.number().nullable(),
  answers: z
    .array(z.array(z.string()).min(3))
    .describe(
      'One row per answer: a 3-element array of strings [questionNumber, answer, confidence] — ' +
        'questionNumber and confidence are numeric values encoded as strings. Arrays only, no field names.'
    ),
})
